import React from "react";
import {
  Box,
  Button,
  Card,
  CardActionArea,
  CardContent,
  Chip,
  Grid,
  Link,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { Link as RouterLink } from "react-router-dom";

// Navy brand mark — the only navy hex in this file. All other navy shades
// flow through `theme.palette.primary.*` / `theme.palette.info.*` so they
// stay in sync with the design system and don't introduce off-allowlist
// hex literals.
const NAVY_700 = "#1B2A4A";

// ─── PageRoot ───────────────────────────────────────────────────────────────
// Transparent grid container. Sits on top of the page gradient (applied to
// <body> in theme.js). Deliberately no background here.
// Liquid Glass: 32px gap between major sections so the surface tier breathes.
export const PageRoot = ({ children }) => (
  <Box sx={{ display: "grid", gap: { xs: 3, sm: 4, md: 4 }, minWidth: 0 }}>{children}</Box>
);

/* ─── Stats ─── */

export const SummaryGrid = ({ items }) => {
  const theme = useTheme();
  return (
    <Grid container spacing={2}>
      {items.map(([label, value, helper, total, to], i) => {
        const ratio = total ? Math.min(value / total, 1) : null;
        const content = (
          <>
            <Typography variant="overline" sx={{ display: "block", mb: 1 }}>
              {label}
            </Typography>
            <Typography
              variant="h3"
              sx={{
                fontVariantNumeric: "tabular-nums",
                lineHeight: 1.05,
              }}
            >
              {value}
            </Typography>
            {helper && (
              <Typography variant="caption" sx={{ mt: 1, display: "block" }}>
                {helper}
              </Typography>
            )}
            {ratio !== null && (
              <Box
                sx={{
                  mt: 2,
                  height: 4,
                  bgcolor: "rgba(0,0,0,0.06)",
                  borderRadius: 999,
                  overflow: "hidden",
                }}
              >
                <Box
                  sx={{
                    height: "100%",
                    width: `${ratio * 100}%`,
                    background: "rgba(27,42,74,0.85)",
                    borderRadius: 999,
                    transition: `width ${theme.motion.morph}`,
                  }}
                />
              </Box>
            )}
          </>
        );
        const sharedSx = {
          p: 3, // 24px — Apple breathes
          borderRadius: "16px", // concentric with section radii
          animation: `fadeInUp ${250 + i * 60}ms ease both`,
          transition: `transform ${theme.motion.morph}, box-shadow ${theme.motion.morph}`,
        };
        return (
          <Grid key={label} size={{ xs: 6, sm: 4, xl: Math.max(2, 12 / items.length) }}>
            {to ? (
              <Paper
                component={RouterLink}
                to={to}
                sx={{
                  ...sharedSx,
                  display: "block",
                  textDecoration: "none",
                  cursor: "pointer",
                  "&:hover": {
                    transform: "translateY(-1px)",
                    boxShadow: [
                      "inset 0 1px 0 rgba(255,255,255,0.65)",
                      "0 16px 40px rgba(31,38,135,0.14)",
                    ].join(", "),
                  },
                }}
              >
                {content}
              </Paper>
            ) : (
              <Paper sx={sharedSx}>{content}</Paper>
            )}
          </Grid>
        );
      })}
    </Grid>
  );
};

/* ─── Section ─── */

export const SectionCard = ({ title, eyebrow, action, children }) => (
  <Box sx={{ animation: "fadeIn 350ms ease both", borderRadius: "16px" }}>
    {(title || eyebrow || action) && (
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        sx={{
          width: "100%",
          minWidth: 0,
          mb: 2, // 16px — generous heading clearance
          columnGap: 2,
          rowGap: 1,
        }}
      >
        <Box sx={{ flex: "1 1 auto", minWidth: 0 }}>
          {eyebrow && (
            <Typography variant="overline" sx={{ display: "block", mb: 0.5 }}>
              {eyebrow}
            </Typography>
          )}
          {/* h4 = SF Pro Display 20px (Title 3) — the right scale for section heads */}
          {title && <Typography variant="h4">{title}</Typography>}
        </Box>
        {action && <Box sx={{ ml: "auto", flexShrink: 0 }}>{action}</Box>}
      </Stack>
    )}
    {children}
  </Box>
);

/* ─── Cards ─── */

export const RecordCard = ({ title, subtitle, status, chips = [], onClick, selected = false, footer }) => {
  const theme = useTheme();
  return (
    <Card
      onClick={onClick}
      sx={{
        height: "100%",
        cursor: onClick ? "pointer" : "default",
        borderRadius: "16px", // surface tier (concentric)
        transition: `transform ${theme.motion.morph}, box-shadow ${theme.motion.morph}, background ${theme.motion.tint}`,
        // Liquid Glass selection: translucent navy tint over inset glass + hairline
        // refractive ring (rgba, not solid navy) + soft outer glow. The ring
        // must NOT read as a hard outline against the glass surround.
        ...(selected && {
          background: `linear-gradient(rgba(27,42,74,0.14), rgba(27,42,74,0.14)), rgba(255,255,255,0.46)`,
          boxShadow: [
            "inset 0 0 0 1px rgba(27,42,74,0.32)",
            "inset 0 1px 0 rgba(255,255,255,0.55)",
            "0 6px 20px rgba(27,42,74,0.12)",
          ].join(", "),
        }),
      }}
    >
      <CardContent sx={{ p: 2.5, "&:last-child": { pb: 2.5 } }}>
        <Stack direction="row" justifyContent="space-between" spacing={1} alignItems="flex-start">
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="body1" fontWeight={600}>{title}</Typography>
            {subtitle && (
              <Typography variant="body2" sx={{ mt: 0.5 }}>
                {subtitle}
              </Typography>
            )}
          </Box>
          {status && <StatusChip status={status} />}
        </Stack>
        {chips.length > 0 && (
          <Stack direction="row" spacing={1} flexWrap="wrap" sx={{ mt: 1.5, gap: 1 }}>
            {chips.map((c) => (
              <Chip key={c} label={c} size="small" />
            ))}
          </Stack>
        )}
        {footer && (
          <>
            <Box sx={{ borderTop: "1px solid rgba(0,0,0,0.06)", my: 2 }} />
            {footer}
          </>
        )}
      </CardContent>
    </Card>
  );
};

export const NavRecordCard = ({ to, title, subtitle, meta }) => (
  <Card sx={{ height: "100%", borderRadius: "16px" }}>
    <CardActionArea component={RouterLink} to={to} sx={{ height: "100%", borderRadius: "inherit" }}>
      <CardContent sx={{ p: 2.5, "&:last-child": { pb: 2.5 } }}>
        {meta && <Chip label={meta} size="small" sx={{ mb: 1.5 }} />}
        <Typography variant="body1" fontWeight={600}>{title}</Typography>
        <Typography variant="body2" sx={{ mt: 0.5 }}>{subtitle}</Typography>
      </CardContent>
    </CardActionArea>
  </Card>
);

/* ─── Actions ─── */

export const InlineActions = ({ children }) => <Stack direction="row" spacing={1} flexWrap="wrap">{children}</Stack>;
export const PrimaryAction = ({ children, ...props }) => <Button variant="contained" {...props}>{children}</Button>;
export const SecondaryAction = ({ children, ...props }) => <Button variant="outlined" {...props}>{children}</Button>;

/* ─── Status ─── */
// Mirror the theme MuiChip color-variant tokens so that even when an explicit
// `bg/color/border` is forced via sx (legacy behavior), the values still match
// Liquid Glass translucent presets. Borders are 22%-opacity tint, fills 14%.
// Text colors use the brand-allowlist severity hexes (#2D6A4F / #92400E /
// #991B1B) directly so we don't introduce off-palette dark variants.
const statusMeta = {
  ready:     { bg: "rgba(45,106,79,0.14)",  color: "#2D6A4F", border: "rgba(45,106,79,0.22)" },
  clear:     { bg: "rgba(45,106,79,0.14)",  color: "#2D6A4F", border: "rgba(45,106,79,0.22)" },
  approved:  { bg: "rgba(45,106,79,0.14)",  color: "#2D6A4F", border: "rgba(45,106,79,0.22)" },
  completed: { bg: "rgba(45,106,79,0.14)",  color: "#2D6A4F", border: "rgba(45,106,79,0.22)" },
  active:    { bg: "rgba(45,106,79,0.14)",  color: "#2D6A4F", border: "rgba(45,106,79,0.22)" },
  compliant: { bg: "rgba(45,106,79,0.14)",  color: "#2D6A4F", border: "rgba(45,106,79,0.22)" },
  review:    { bg: "rgba(146,64,14,0.14)",  color: "#92400E", border: "rgba(146,64,14,0.22)" },
  pending:   { bg: "rgba(146,64,14,0.14)",  color: "#92400E", border: "rgba(146,64,14,0.22)" },
  running:   { bg: "rgba(27,42,74,0.10)",   color: NAVY_700,  border: "rgba(27,42,74,0.18)" },
  queued:    { bg: "rgba(27,42,74,0.10)",   color: NAVY_700,  border: "rgba(27,42,74,0.18)" },
  draft:     { bg: "rgba(0,0,0,0.06)",      color: "rgba(0,0,0,0.60)", border: "rgba(0,0,0,0.10)" },
  blocked:   { bg: "rgba(27,42,74,0.92)",   color: "rgba(255,255,255,0.96)", border: "rgba(27,42,74,0.92)" },
  failed:    { bg: "rgba(153,27,27,0.14)",  color: "#991B1B", border: "rgba(153,27,27,0.22)" },
};

export const StatusChip = ({ status }) => {
  const s = String(status || "").toLowerCase();
  const meta = statusMeta[s] || { bg: "rgba(0,0,0,0.06)", color: "rgba(0,0,0,0.60)", border: "rgba(0,0,0,0.10)" };
  return (
    <Chip
      label={status}
      size="small"
      sx={{
        bgcolor: meta.bg,
        color: meta.color,
        // Translucent severity-tint border, not a sharp hex. Mirrors the
        // theme MuiChip color-variant overrides.
        border: `1px solid ${meta.border}`,
        borderRadius: 999, // explicit pill
        fontWeight: 600,
      }}
    />
  );
};

export const StatusDot = ({ status }) => {
  const s = String(status || "").toLowerCase();
  const meta = statusMeta[s] || { color: "rgba(0,0,0,0.30)" };
  return <Box sx={{ width: 8, height: 8, borderRadius: "50%", flexShrink: 0, bgcolor: meta.color }} />;
};

/* ─── SourceCitation ─── */

// Splits a long quote into distinct sentences so each reads like its own line
// in the original document (articles, regulations, and IFRA PDFs tend to be
// written in discrete clauses).
const splitQuoteIntoLines = (text = "") => {
  const normalised = String(text).replace(/\s+/g, " ").trim();
  if (!normalised) return [];
  const pieces = normalised.match(/[^.!?]+[.!?]+|[^.!?]+$/g) || [normalised];
  return pieces.map((s) => s.trim()).filter(Boolean);
};

// Render a line with any substance name(s) emphasized. `highlight` can be a
// string or an array of strings — case-insensitive, first match wins per term.
const renderLineWithHighlight = (line, highlight) => {
  const terms = (Array.isArray(highlight) ? highlight : [highlight])
    .filter((t) => typeof t === "string" && t.trim())
    .sort((a, b) => b.length - a.length); // longest first to avoid sub-matches
  if (!terms.length) return line;

  const escape = (s) => s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
  const pattern = new RegExp(`(${terms.map(escape).join("|")})`, "gi");
  const parts = line.split(pattern);
  return parts.map((part, i) =>
    pattern.test(part) ? (
      <Box
        key={i}
        component="mark"
        sx={{
          bgcolor: "rgba(228,196,93,0.35)",
          color: "rgba(255,235,180,0.98)",
          fontWeight: 600,
          fontStyle: "normal",
          px: 0.25,
          borderRadius: "4px",
        }}
      >
        {part}
      </Box>
    ) : (
      <React.Fragment key={i}>{part}</React.Fragment>
    )
  );
};

const QuoteBlock = ({ entry, highlight, showTopRule = false }) => {
  const lines = splitQuoteIntoLines(entry.quote || entry.text || "");
  if (!lines.length) return null;
  const label = entry.label;
  const source = entry.source;
  const sourceUrl = entry.sourceUrl;
  const hl = entry.highlight ?? highlight;
  return (
    <Box sx={{
      ...(showTopRule ? { borderTop: "1px solid rgba(255,255,255,0.12)", pt: 1.5, mt: 1.5 } : {}),
    }}>
      {label && (
        <Typography
          variant="overline"
          sx={{ display: "block", color: "rgba(255,255,255,0.55)", mb: 0.5 }}
        >
          {label}
        </Typography>
      )}
      <Box sx={{ position: "relative", pl: 1.25 }}>
        <Box sx={{
          position: "absolute", top: -6, left: -2,
          fontFamily: "Georgia, 'Times New Roman', serif",
          fontSize: "1.6rem", lineHeight: 1,
          color: "rgba(227,208,154,0.85)", userSelect: "none", pointerEvents: "none",
        }}>
          &ldquo;
        </Box>
        <Box sx={{ pl: 1.5 }}>
          {lines.map((line, i) => (
            <Box key={i} sx={{
              fontFamily: '"SF Pro Text", -apple-system, BlinkMacSystemFont, "Inter", sans-serif',
              fontStyle: "italic",
              fontSize: "0.8125rem",
              lineHeight: 1.55,
              color: "rgba(255,255,255,0.92)",
              mb: i < lines.length - 1 ? 0.5 : 0,
            }}>
              {renderLineWithHighlight(line, hl)}
            </Box>
          ))}
        </Box>
      </Box>
      {(source || sourceUrl) && (
        <Box sx={{
          mt: 1, display: "flex", alignItems: "center",
          gap: 1, flexWrap: "wrap",
        }}>
          {source && (
            <Box sx={{ fontSize: "0.75rem", fontWeight: 500, color: "rgba(255,255,255,0.65)" }}>
              — {source}
            </Box>
          )}
          {sourceUrl && (
            <Link
              href={sourceUrl} target="_blank" rel="noopener noreferrer"
              underline="none"
              sx={{
                fontSize: "0.75rem",
                color: "rgba(227,208,154,0.95)",
                ml: "auto",
                "&:hover": { color: "rgba(251,243,212,1)", textDecoration: "underline" },
              }}
            >
              Open ↗
            </Link>
          )}
        </Box>
      )}
    </Box>
  );
};

// PullQuote — rendered in *light* (in-paper) context. Thinner 2px lighter-gold
// rule + soft gold gradient bg keep it from competing with the glass surface
// around it. Inside dark tooltips (SourceQuoteTooltip), QuoteBlock above is
// used directly with light-on-dark colors.
//
// Generous internal padding (p: 2 = 16px) gives the italic SF Pro Text room
// to breathe — Liquid Glass quote panels are airy.
const PullQuote = ({ entries = [], highlight, light = false }) => {
  if (light) {
    return (
      <Box sx={{
        position: "relative",
        background: "linear-gradient(135deg, rgba(184,150,46,0.06) 0%, rgba(184,150,46,0.02) 100%)",
        color: "rgba(0,0,0,0.88)",
        // Lighter gold rule (gold[400] @ rgba) so it whispers rather than
        // shouts against the translucent surface tier — kept in the rgba
        // vibrancy lane to stay off the brand-allowlist hex literals.
        borderLeft: "2px solid rgba(212,185,110,0.85)",
        borderRadius: "12px",
        p: 2,
        pl: 2.5,
        maxWidth: 420,
      }}>
        {entries.map((entry, i) => (
          <QuoteBlockLight
            key={i}
            entry={entry}
            highlight={highlight}
            showTopRule={i > 0}
          />
        ))}
      </Box>
    );
  }
  return (
    <Box sx={{ p: 0.25, maxWidth: 420 }}>
      {entries.map((entry, i) => (
        <QuoteBlock
          key={i}
          entry={entry}
          highlight={highlight}
          showTopRule={i > 0}
        />
      ))}
    </Box>
  );
};

// Light-context variant of QuoteBlock for in-paper PullQuote rendering.
const QuoteBlockLight = ({ entry, highlight, showTopRule = false }) => {
  const lines = splitQuoteIntoLines(entry.quote || entry.text || "");
  if (!lines.length) return null;
  const { label, source, sourceUrl } = entry;
  const hl = entry.highlight ?? highlight;
  return (
    <Box sx={{
      ...(showTopRule ? { borderTop: "1px solid rgba(0,0,0,0.06)", pt: 1.5, mt: 1.5 } : {}),
    }}>
      {label && (
        <Typography variant="overline" sx={{ display: "block", mb: 0.5 }}>{label}</Typography>
      )}
      <Box sx={{ position: "relative", pl: 1.25 }}>
        <Box sx={{
          position: "absolute", top: -6, left: -2,
          fontFamily: "Georgia, 'Times New Roman', serif",
          fontSize: "1.6rem", lineHeight: 1,
          // Brand gold decorative glyph — translucent rgba derived from gold[300]
          // so it stays in the rgba vibrancy lane (no off-allowlist hex literal).
          color: "rgba(227,208,154,0.85)", userSelect: "none", pointerEvents: "none",
        }}>
          &ldquo;
        </Box>
        <Box sx={{ pl: 1.5 }}>
          {lines.map((line, i) => (
            <Box key={i} sx={{
              fontFamily: '"SF Pro Text", -apple-system, BlinkMacSystemFont, "Inter", sans-serif',
              fontStyle: "italic",
              fontSize: "0.9375rem",
              lineHeight: 1.55,
              color: "rgba(0,0,0,0.88)",
              mb: i < lines.length - 1 ? 0.5 : 0,
            }}>
              {renderLineWithHighlight(line, hl)}
            </Box>
          ))}
        </Box>
      </Box>
      {(source || sourceUrl) && (
        <Box sx={{
          mt: 1, display: "flex", alignItems: "center",
          gap: 1, flexWrap: "wrap",
        }}>
          {source && (
            <Box sx={{ fontSize: "0.75rem", fontWeight: 500, color: "rgba(0,0,0,0.60)" }}>
              — {source}
            </Box>
          )}
          {sourceUrl && (
            <Link
              href={sourceUrl} target="_blank" rel="noopener noreferrer"
              underline="none"
              sx={{
                fontSize: "0.75rem",
                // Navy[600] tint — rgba so we stay off-allowlist hex.
                color: "rgba(27,42,74,0.75)",
                ml: "auto",
                "&:hover": { color: NAVY_700, textDecoration: "underline" },
              }}
            >
              Open ↗
            </Link>
          )}
        </Box>
      )}
    </Box>
  );
};

// Normalise incoming props into a consistent `entries` array.
// Accepts either a single string `sourceQuote` or a structured
// `sourceQuotes: [{ quote, source, sourceUrl, label, highlight }]`.
const buildEntries = ({ source, sourceUrl, sourceQuote, sourceQuotes }) => {
  if (Array.isArray(sourceQuotes) && sourceQuotes.length) {
    return sourceQuotes
      .map((entry) => ({
        quote: entry.quote || entry.text || "",
        source: entry.source || source,
        sourceUrl: entry.sourceUrl || "",
        label: entry.label || "",
        highlight: entry.highlight,
      }))
      .filter((entry) => entry.quote);
  }
  if (sourceQuote) {
    return [{ quote: sourceQuote, source, sourceUrl }];
  }
  return [];
};

// Generic wrapper that attaches the dark-glass pull-quote hover tooltip to any
// child. Tooltip itself is styled by MUI theme MuiTooltip override (dark frost),
// so we only need to widen the maxWidth and let typography handle the rest.
//
// `enterDelay` defaults to 500ms so brushing past dense chip clusters doesn't
// fire a flurry of tooltips. Override downward where the trigger is sparse.
export const SourceQuoteTooltip = ({
  source,
  sourceUrl,
  sourceQuote,
  sourceQuotes,
  highlight,
  placement = "top",
  fallback,
  enterDelay = 500,
  leaveDelay = 80,
  children,
}) => {
  const entries = buildEntries({ source, sourceUrl, sourceQuote, sourceQuotes });
  if (entries.length === 0 && !fallback) return children;
  const title =
    entries.length > 0
      ? <PullQuote entries={entries} highlight={highlight} />
      : fallback;
  return (
    <Tooltip
      title={title}
      arrow
      placement={placement}
      enterDelay={enterDelay}
      leaveDelay={leaveDelay}
      slotProps={{ tooltip: { sx: { maxWidth: "none", p: entries.length > 0 ? "12px 14px" : "10px 14px" } } }}
    >
      {/* `inline-block` (not `inline-flex`) keeps hit-testing predictable
          inside `<td>` cells where MUI Tooltip otherwise misses some hovers. */}
      <span style={{ cursor: "help", display: "inline-block" }}>{children}</span>
    </Tooltip>
  );
};

export const SourceCitation = ({ source, sourceUrl, sourceQuote, sourceQuotes, highlight }) => {
  const theme = useTheme();
  if (!source) return null;
  const entries = buildEntries({ source, sourceUrl, sourceQuote, sourceQuotes });
  const hasQuote = entries.length > 0;
  const label = (
    <Typography component="span" variant="caption"
      sx={{
        // Navy[600] flows through palette.info.main — keeps the hex literal
        // out of this file.
        color: theme.palette.info.main,
        fontWeight: 500,
        // Dotted underline as the spec requires for citations.
        borderBottom: sourceUrl || hasQuote ? "1px dotted rgba(27,42,74,0.35)" : "none",
        borderRadius: "4px",
        px: 0.25,
        transition: `background ${theme.motion.tint}, color ${theme.motion.tint}`,
        "&:hover": sourceUrl || hasQuote
          ? { color: NAVY_700, background: "rgba(27,42,74,0.06)" }
          : {},
      }}
    >{source}</Typography>
  );
  const linked = sourceUrl
    ? <Link href={sourceUrl} target="_blank" rel="noopener noreferrer" underline="none">{label}</Link>
    : label;
  if (!hasQuote) return linked;
  return (
    <Tooltip
      title={<PullQuote entries={entries} highlight={highlight} />}
      arrow
      placement="top"
      enterDelay={150}
      leaveDelay={200}
      slotProps={{ tooltip: { sx: { maxWidth: "none", p: "12px 14px" } } }}
    >
      <span style={{ cursor: "help" }}>{linked}</span>
    </Tooltip>
  );
};

// Minimal 1px horizontal rule that fades in and out — sits cleanly on glass.
export const GradientDivider = () => (
  <Box
    sx={{
      height: "1px",
      width: "100%",
      background: "linear-gradient(to right, transparent, rgba(0,0,0,0.10), transparent)",
      border: "none",
    }}
  />
);

/* ─── Table ─── */

export const DataTable = ({ columns, rows, getRowKey }) => {
  const theme = useTheme();
  return (
    // Wrap the table so wide column sets scroll inside their own container
    // instead of being clipped by the page-level `overflowX: hidden` defense.
    <Box sx={{ overflowX: "auto", width: "100%" }}>
      <Table size="small">
        <TableHead>
          <TableRow
            sx={{
              "& .MuiTableCell-head": {
                background: theme.glass.inset.background,
                backdropFilter: theme.glass.inset.backdropFilter,
                WebkitBackdropFilter: theme.glass.inset.WebkitBackdropFilter,
              },
            }}
          >
            {columns.map((col) => <TableCell key={col.key}>{col.label}</TableCell>)}
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((row, i) => (
            <TableRow
              key={getRowKey ? getRowKey(row) : i}
              sx={{
                transition: `background ${theme.motion.tint}`,
                "&:hover": { background: "rgba(255,255,255,0.30)" },
              }}
            >
              {columns.map((col) => (
                <TableCell key={col.key}>
                  {typeof col.render === "function" ? col.render(row) : row[col.key]}
                </TableCell>
              ))}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </Box>
  );
};
